import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { Dropzone } from "@/components/ui/customs/dropzone-file";
import { useCreateProductUsa, useUpdateProductUsa } from "@/api/productUsa.api";
import { useGetAllImporters } from "@/api/importer.api";
import { useGetCompanyBoListV1 } from "@/api/company.api";
import { useCreateFileBo } from "@/api/file.api";
import { getFile } from "@/api/s3.api";
import { ProductUSA } from "@/types/api/api-types";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import { WineColorEnum } from "@/constants/enums/wineColor.enum";
import { volumesOptionsCola } from "@/constants/options/volumes.option";
import { productUsaFormSchema, type ProductUsaFormData } from "./productFormSchema";
import { ControlledCountryComboboxCountryCode } from "@/components/ui/customs/controlled-combobox-countryCode";
import { useWineColorOptions } from "@/hooks/useEnumOptions";
import { useTranslation } from "react-i18next";
import { useTranslateMessage } from "@/hooks/useTranslateMessage";

type Props = {
  open: boolean;
  onClose: () => void;
  product?: ProductUSA | null;
};

export const ProductUsaFormModal = ({ open, onClose, product }: Props) => {
  const { t } = useTranslation(["boCatalog", "common"]);
  const translateMessage = useTranslateMessage();
  const wineColorOptions = useWineColorOptions();
  const isEdit = !!product;

  const { mutateAsync: createProductUsa, isPending: isCreating } = useCreateProductUsa();
  const { mutateAsync: updateProductUsa, isPending: isUpdating } = useUpdateProductUsa();
  const { mutateAsync: createFile } = useCreateFileBo();
  const { data: importers } = useGetAllImporters();
  const { data: companies } = useGetCompanyBoListV1();

  const [colaFiles, setColaFiles] = useState<File[] | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [replacingCola, setReplacingCola] = useState(false);

  const isPending = isCreating || isUpdating || isUploading;

  const importerOptions = useMemo(
    () => (importers?.data || []).map((i) => ({ label: i.name, value: i.id })),
    [importers],
  );

  const companyOptions = useMemo(
    () => (companies?.data || []).map((c) => ({ label: c.businessName, value: c.id })),
    [companies],
  );

  const volumeSelectOptions = useMemo(
    () => volumesOptionsCola.map((v) => ({ label: v.label, value: String(v.value) })),
    [],
  );

  const { control, handleSubmit, reset, setValue, formState } = useForm<ProductUsaFormData>({
    resolver: zodResolver(productUsaFormSchema),
    defaultValues: {
      description: "",
      descriptionPelican: "",
      countryOfOrigin: "",
      harmonizedCode: "",
      color: undefined as unknown as WineColorEnum,
      alcoholPercentage: undefined,
      volume: undefined,
      ttbNumber: "",
      importerId: "",
      clientId: "",
      colaId: "",
    },
  });

  useEffect(() => {
    if (!open) return;
    setColaFiles(null);
    setReplacingCola(false);
    if (product) {
      reset({
        description: product.description,
        descriptionPelican: product.descriptionPelican,
        countryOfOrigin: product.countryOfOrigin,
        harmonizedCode: product.harmonizedCode,
        color: product.color as WineColorEnum,
        alcoholPercentage: product.alcoholPercentage,
        volume: product.volume,
        ttbNumber: product.ttbNumber,
        importerId: product.importer.id,
        clientId: product.client?.id || "",
        colaId: product.cola.id,
      });
    } else {
      reset({
        description: "",
        descriptionPelican: "",
        countryOfOrigin: "",
        harmonizedCode: "",
        color: undefined as unknown as WineColorEnum,
        alcoholPercentage: undefined,
        volume: undefined,
        ttbNumber: "",
        importerId: "",
        clientId: "",
        colaId: "",
      });
    }
  }, [open, product, reset]);

  const uploadColaFile = async (file: File): Promise<string> => {
    const contentType = file.type || "application/pdf";
    const { uploadUrl, fileId } = await createFile({
      fileName: file.name,
      type: FileTypeEnum.PRODUCT_COLA,
      contentType,
    });

    await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": contentType },
    });

    return fileId;
  };

  const onSubmit = async (data: ProductUsaFormData) => {
    try {
      setIsUploading(true);

      let colaId = data.colaId;

      // Upload COLA file if a new file was selected
      if (colaFiles && colaFiles.length > 0) {
        colaId = await uploadColaFile(colaFiles[0]);
      }

      const payload = {
        ...data,
        clientId: data.clientId || "",
        colaId,
      };

      if (isEdit) {
        await updateProductUsa({ id: product.id, payload });
      } else {
        await createProductUsa(payload);
      }
      onClose();
    } catch {
      toast.error(t("productUsaFormModal.colaUploadError"));
    } finally {
      setIsUploading(false);
    }
  };

  const handleColaFilesChange = (
    files: File[] | null | ((prev: File[] | null) => File[] | null),
  ) => {
    const resolved = typeof files === "function" ? files(colaFiles) : files;
    setColaFiles(resolved);
    if (resolved && resolved.length > 0) {
      setValue("colaId", "pending-upload", { shouldValidate: true });
    } else if (!isEdit) {
      setValue("colaId", "", { shouldValidate: true });
    }
  };

  const handleViewCola = async () => {
    if (!product?.cola.path) return;
    const downloadUrl = await getFile(product.cola.path);
    window.open(downloadUrl, "_blank");
  };

  const handleCancelReplace = () => {
    setReplacingCola(false);
    setColaFiles(null);
    if (product) {
      setValue("colaId", product.cola.id, { shouldValidate: true });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t("productUsaFormModal.editTitle") : t("productUsaFormModal.createTitle")}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            <ControlledUnifiedInput
              control={control}
              name="description"
              label={t("productUsaFormModal.descriptionLabel")}
              wrapperClassName="col-span-2"
            />
            <ControlledUnifiedInput
              control={control}
              name="descriptionPelican"
              label={t("productUsaFormModal.descriptionPelicanLabel")}
              wrapperClassName="col-span-2"
            />
            <ControlledCountryComboboxCountryCode
              control={control}
              name="countryOfOrigin"
              label={t("productUsaFormModal.countryOfOriginLabel")}
            />
            <ControlledUnifiedInput
              control={control}
              name="harmonizedCode"
              label={t("productUsaFormModal.harmonizedCodeLabel")}
            />
            <ControlledSelectV2
              control={control}
              name="color"
              label={t("productUsaFormModal.colorLabel")}
              options={wineColorOptions}
              placeholder=""
            />
            <ControlledSelectV2
              control={control}
              name="volume"
              label={t("productUsaFormModal.volumeLabel")}
              options={volumeSelectOptions}
              setValue={(value) => setValue("volume", parseFloat(value))}
              placeholder=""
            />
            <ControlledUnifiedInput
              control={control}
              name="alcoholPercentage"
              label={t("productUsaFormModal.alcoholPercentageLabel")}
              inputType="float"
            />
            <ControlledUnifiedInput
              control={control}
              name="ttbNumber"
              label={t("productUsaFormModal.ttbNumberLabel")}
            />
            <div className="col-span-2 flex flex-col gap-2">
              <span className="text-sm font-medium">
                {t("productUsaFormModal.colaDocumentLabel")}
              </span>
              {isEdit && product.cola && !replacingCola ? (
                <div className="flex items-center justify-between gap-3 rounded-lg border bg-gray-50 p-3">
                  <p className="truncate text-sm font-medium text-gray-700">{product.cola.name}</p>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      className="rounded p-1 text-gray-600 hover:text-gray-800 focus:outline-hidden"
                      onClick={handleViewCola}
                      title={t("productUsaFormModal.viewColaFile")}
                    >
                      <ExternalLink className="size-4" />
                    </button>
                    <button
                      type="button"
                      className="rounded p-1 text-gray-600 hover:text-blue-600 focus:outline-hidden"
                      onClick={() => setReplacingCola(true)}
                      title={t("productUsaFormModal.replaceColaFile")}
                      disabled={isPending}
                    >
                      <RefreshCw className="size-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <Dropzone
                    files={colaFiles}
                    setFiles={handleColaFilesChange}
                    maxFiles={1}
                    dropZoneText={t("productUsaFormModal.colaDropzone")}
                    accept={{ "application/pdf": [".pdf"] }}
                  />
                  {isEdit && replacingCola && (
                    <button
                      type="button"
                      className="self-start text-xs text-gray-500 underline hover:text-gray-700"
                      onClick={handleCancelReplace}
                    >
                      {t("productUsaFormModal.cancelReplace")}
                    </button>
                  )}
                  {formState.errors.colaId && !colaFiles?.length && !isEdit && (
                    <p className="text-xs text-red-500">
                      {translateMessage(formState.errors.colaId.message)}
                    </p>
                  )}
                </>
              )}
            </div>
            <ControlledSelectV2
              control={control}
              name="importerId"
              label={t("productUsaFormModal.importerLabel")}
              options={importerOptions}
              searchable
              searchPlaceholder={t("productUsaFormModal.importerSearchPlaceholder")}
              wrapperClassName="col-span-2"
              placeholder=""
            />
            <ControlledSelectV2
              control={control}
              name="clientId"
              label={t("productUsaFormModal.clientLabel")}
              options={companyOptions}
              searchable
              searchPlaceholder={t("productUsaFormModal.clientSearchPlaceholder")}
              clearable
              wrapperClassName="col-span-2"
              placeholder=""
            />
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              {t("common:actions.cancel")}
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? (
                <Loader2 className="animate-spin" />
              ) : isEdit ? (
                t("productUsaFormModal.save")
              ) : (
                t("productUsaFormModal.create")
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
